// SearchingModal — dimmed scrim with a centered card showing search
// progress while the matchmaker groups parties. Title swaps from
// "Searching..." to "{found}/{needed} players found" once outsider
// players are in the search registry. Two buttons: Cancel + Join vs NPCs.
//
// Mounts into #match-modals (shared root) at z-index 200, on top of any
// in-game modal but below the ConfirmationPopup (z-index 220).

import { MenuNav, type MenuItem } from '../MenuNav';
import { menuAudio } from '../MenuAudio';
import { makeSpinner } from '../components/Spinner';
import { t } from '../../i18n';

const STYLES_ID = 'searching-modal-styles';
const STYLES = `
@import url('https://fonts.googleapis.com/css2?family=Chewy&display=swap');

.searching-modal {
  position: fixed; inset: 0;
  display: flex; align-items: center; justify-content: center;
  background: rgba(0, 0, 0, 0.55);
  backdrop-filter: blur(8px);
  z-index: 200;
  font-family: 'Chewy', ui-sans-serif, system-ui, sans-serif;
  color: #FFFFFF;
  opacity: 0;
  transition: opacity 200ms ease;
  pointer-events: auto;
}
.searching-modal.shown { opacity: 1; }
.searching-modal.closing { opacity: 0; }

.searching-modal .card {
  pointer-events: auto;
  background: #1E232C;
  border-radius: 18px;
  box-shadow: 0 16px 48px rgba(0,0,0,0.5);
  padding: 32px 36px;
  display: flex; flex-direction: column; align-items: center;
  gap: 20px;
  min-width: 480px; max-width: 90vw;
  transform: scale(0.96);
  transition: transform 200ms cubic-bezier(0.2, 0.9, 0.2, 1);
}
.searching-modal.shown .card { transform: scale(1); }

.searching-modal .title-row {
  display: flex; align-items: center; gap: 16px;
  font-size: 36px;
  -webkit-text-stroke: 3px rgba(0,0,0,0.22);
  paint-order: stroke fill;
}
.searching-modal .title-row .spin {
  width: 26px; height: 26px;
  border: 3px solid rgba(255,255,255,0.4);
  border-top-color: #FFFFFF;
  border-radius: 50%;
  animation: searching-rotate 900ms linear infinite;
}
@keyframes searching-rotate { to { transform: rotate(360deg); } }

.searching-modal .timer {
  font-size: 22px;
  color: rgba(255,255,255,0.7);
}

.searching-modal .actions {
  display: flex; gap: 14px;
  margin-top: 4px;
}
.searching-modal .btn {
  border: 0; cursor: pointer;
  font-family: 'Chewy', sans-serif;
  font-size: 22px;
  padding: 12px 26px;
  border-radius: 14px;
  transition: transform 120ms ease, box-shadow 120ms ease, filter 120ms ease;
}
.searching-modal .btn.cancel {
  color: #1E232C;
  background: linear-gradient(180deg, #F1F9FB 0%, #C3D6DB 100%);
  box-shadow: 0 4px 0 #ADC5CC, 0 6px 14px rgba(0,0,0,0.25);
}
.searching-modal .btn.npc {
  color: #FFFFFF;
  -webkit-text-stroke: 3px #356B8C;
  paint-order: stroke fill;
  background: linear-gradient(180deg, #7DD2F5 0%, #3DA6FF 100%);
  box-shadow: 0 4px 0 #256AA0, 0 8px 18px rgba(0,0,0,0.3);
}
.searching-modal .btn:hover,
.searching-modal .btn.focused { filter: brightness(1.04); outline: 4px solid rgba(255,255,255,0.6); outline-offset: 3px; }
.searching-modal .btn:active { transform: translateY(3px); }
.searching-modal .btn.waiting {
  filter: grayscale(0.45) brightness(0.85);
  cursor: default;
  pointer-events: none;
}
.searching-modal .btn.npc.hidden { display: none; }

.searching-modal .footnote {
  font-size: 18px;
  color: rgba(255,255,255,0.7);
  text-align: center;
  margin-top: 4px;
  max-width: 360px;
}

.searching-modal .spinner-host { margin-top: 4px; }
`;

export interface SearchingModalCallbacks {
  onCancel(): void;
  onJoinNpcsNow(): void;
}

export interface SearchingModalOptions {
  /** Hide the "Join vs NPCs" button entirely. Only party owners are
   *  authorised to request fill — non-leaders see Cancel only. */
  canRequestNpcFill?: boolean;
}

export interface SearchingModalHandle {
  setProgress(humansFound: number, humansNeeded: number): void;
  setSeconds(seconds: number): void;
  /** Once the local owner has consented to NPC fill, swap the title to
   *  "Waiting for other parties..." and dim the NPC button — the server
   *  needs every party leader to consent before fill triggers. */
  setWaitingForOthers(waiting: boolean): void;
  /** Owner status can change live (owner transfer mid-search). Toggle
   *  the NPC button visibility without dismissing the modal. */
  setCanRequestNpcFill(can: boolean): void;
  dismiss(): void;
}

export function showSearchingModal(
  callbacks: SearchingModalCallbacks,
  opts: SearchingModalOptions = {}
): SearchingModalHandle {
  ensureStyles();
  const root = ensureRoot();
  const wrap = document.createElement('div');
  wrap.className = 'searching-modal';
  wrap.innerHTML = `
    <div class="card">
      <div class="title-row">
        <div class="spin"></div>
        <div class="title-text">${t('searching.searching')}</div>
      </div>
      <div class="timer">${t('searching.timer', { seconds: 0 })}</div>
      <div class="actions">
        <button type="button" class="btn cancel">${t('searching.cancel')}</button>
        <button type="button" class="btn npc">${t('searching.joinNpcs')}</button>
      </div>
      <div class="footnote">${t('searching.npcFootnote')}</div>
    </div>
  `;
  // Insert spinner inside the card after the timer for a denser visual.
  const spinnerHost = document.createElement('div');
  spinnerHost.className = 'spinner-host';
  spinnerHost.appendChild(makeSpinner({ width: 280 }));
  const card = wrap.querySelector('.card') as HTMLElement;
  card.insertBefore(spinnerHost, card.querySelector('.actions'));

  root.appendChild(wrap);
  requestAnimationFrame(() => wrap.classList.add('shown'));

  let dismissed = false;

  const titleEl = wrap.querySelector('.title-text') as HTMLElement;
  const timerEl = wrap.querySelector('.timer') as HTMLElement;
  const cancelBtn = wrap.querySelector('.btn.cancel') as HTMLButtonElement;
  const npcBtn = wrap.querySelector('.btn.npc') as HTMLButtonElement;

  let lastFound = 0;
  let lastNeeded = 0;
  let lastSeconds = 0;
  let waitingForOthers = false;
  let canFill = opts.canRequestNpcFill ?? true;

  // Apply initial owner state.
  if (!canFill) npcBtn.classList.add('hidden');

  const finish = (which: 'cancel' | 'external'): void => {
    if (dismissed) return;
    dismissed = true;
    nav?.dispose();
    removeEventListener('keydown', onKeyDown, { capture: true } as EventListenerOptions);
    wrap.classList.add('closing');
    setTimeout(() => wrap.remove(), 220);
    if (which === 'cancel') callbacks.onCancel();
  };

  // The "Join vs NPCs" path does NOT dismiss the modal — the user has
  // consented but every other party leader in the matching group must
  // also consent before the matchmaker actually fills. Keep the modal
  // up showing "Waiting for other parties...". Cancel still works to
  // bail entirely.
  const requestNpcFill = (): void => {
    if (waitingForOthers) return;
    callbacks.onJoinNpcsNow();
    waitingForOthers = true;
    npcBtn.classList.add('waiting');
    renderTitle();
  };

  const buildNavItems = (): MenuItem[] => {
    const out: MenuItem[] = [
      { el: cancelBtn, row: 0, col: 0, onActivate: () => finish('cancel') }
    ];
    if (canFill) {
      out.push({ el: npcBtn, row: 0, col: 1, onActivate: requestNpcFill });
    }
    return out;
  };
  // Default focus on NPC option when available (more "actionable" than
  // Cancel), else on Cancel. Matches the mockup which highlights the
  // primary action.
  let nav: MenuNav | null = new MenuNav({
    items: buildNavItems(),
    initialIndex: canFill ? 1 : 0
  });
  nav.mount();

  const onKeyDown = (e: KeyboardEvent): void => {
    if (dismissed) return;
    if (e.code === 'Escape' || e.code === 'Backspace') {
      e.preventDefault();
      e.stopPropagation();
      menuAudio.playClick();
      finish('cancel');
    }
  };
  addEventListener('keydown', onKeyDown, { capture: true });

  const renderTitle = (): void => {
    if (waitingForOthers) {
      titleEl.textContent = t('searching.waitingForOthers');
    } else if (lastFound > 0 && lastNeeded > 0) {
      titleEl.textContent = t('searching.playersFound', { found: lastFound, needed: lastNeeded });
    } else {
      titleEl.textContent = t('searching.searching');
    }
  };
  const renderTimer = (): void => {
    timerEl.textContent = t('searching.timer', { seconds: lastSeconds });
  };

  return {
    setProgress(humansFound: number, humansNeeded: number): void {
      lastFound = humansFound;
      lastNeeded = humansNeeded;
      renderTitle();
    },
    setSeconds(seconds: number): void {
      lastSeconds = Math.max(0, Math.floor(seconds));
      renderTimer();
    },
    setWaitingForOthers(waiting: boolean): void {
      waitingForOthers = waiting;
      npcBtn.classList.toggle('waiting', waiting);
      renderTitle();
    },
    setCanRequestNpcFill(can: boolean): void {
      if (canFill === can) return;
      canFill = can;
      npcBtn.classList.toggle('hidden', !can);
      // Rebuild MenuNav so the now-hidden item isn't navigable. Preserve
      // current focus on Cancel by default.
      nav?.dispose();
      nav = new MenuNav({
        items: buildNavItems(),
        initialIndex: 0
      });
      nav.mount();
    },
    dismiss(): void { finish('external'); }
  };
}

function ensureRoot(): HTMLElement {
  let root = document.getElementById('match-modals');
  if (!root) {
    root = document.createElement('div');
    root.id = 'match-modals';
    root.style.cssText = `position:fixed; inset:0; z-index:150; pointer-events:none;`;
    document.body.appendChild(root);
  }
  return root;
}

function ensureStyles(): void {
  if (document.getElementById(STYLES_ID)) return;
  const style = document.createElement('style');
  style.id = STYLES_ID;
  style.textContent = STYLES;
  document.head.appendChild(style);
}
